/**
 * Evidence pack (preview / ZIP / email) on the Compliance page.
 *
 * WHY IT IS ALSO HERE
 * The pack already existed, but only inside a customer record. So the one
 * artefact an auditor actually asks for — dated evidence for a period — was
 * reachable only if you already knew to open a customer and scroll. Someone told
 * "send me your evidence for Q2" goes to the page called Compliance and finds a
 * framework score, which is a different thing.
 *
 * Same failure B1 exists to fix, one level down: the feature was fine, its
 * location was wrong. This is a second entry point, not a second implementation
 * — identical endpoints, so there is no behaviour to drift.
 *
 * The pack is inherently per-customer (it needs a customer and a period), so
 * when the header is set to all customers this explains that rather than
 * offering a button that cannot work.
 */
import { useMemo, useState } from "react";

import api, { downloadFile } from "../api/client";

/** Current quarter plus the previous four, newest first. */
function usePeriods(): string[] {
  return useMemo(() => {
    const now = new Date();
    let y = now.getFullYear();
    let q = Math.floor(now.getMonth() / 3) + 1;
    const out = [`${y}-Q${q}`];
    for (let i = 0; i < 4; i++) {
      q -= 1;
      if (q < 1) {
        q = 4;
        y -= 1;
      }
      out.push(`${y}-Q${q}`);
    }
    return out;
  }, []);
}

export default function ComplianceEvidencePack({
  customerId,
  customerName,
}: {
  customerId: number | "";
  customerName?: string | null;
}) {
  const periods = usePeriods();
  // Default to the LAST complete quarter, not the current one: a pack for a
  // quarter that hasn't finished is incomplete evidence, and that is the
  // mistake worth defaulting away from.
  const [period, setPeriod] = useState(periods[1]);
  const [busy, setBusy] = useState<"" | "preview" | "zip" | "email">("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [emailTo, setEmailTo] = useState("");

  const scoped = customerId !== "";

  const preview = async () => {
    setErr("");
    setBusy("preview");
    try {
      const r = await api.get("/api/v1/compliance/preview", {
        params: { customer_id: customerId, period },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // Revoked on a timer rather than immediately: the new tab still needs the
      // blob after this handler returns.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Couldn't generate the preview");
    } finally {
      setBusy("");
    }
  };

  const zip = async () => {
    setErr("");
    setBusy("zip");
    try {
      await downloadFile(
        "/api/v1/compliance/download.zip",
        `Compliance_${customerName || "customer"}_${period}.zip`,
        { customer_id: customerId, period },
      );
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Couldn't build the ZIP");
    } finally {
      setBusy("");
    }
  };

  const email = async () => {
    setErr("");
    setNote("");
    setBusy("email");
    try {
      const body: Record<string, unknown> = { customer_id: Number(customerId), period };
      if (emailTo.trim()) body.to_email = emailTo.trim();
      const r = await api.post("/api/v1/compliance/email", body);
      setNote(`Sent to ${r.data.recipient} (${r.data.period})`);
      setEmailTo("");
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Couldn't send the pack");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <h2 className="font-semibold">Evidence pack</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Dated evidence for one customer and one period: access reviews, an audit-log
            slice, patch state, alert history, SLA performance and self-healing activity.
            Rule-based and reproducible — unlike the assessment above, no AI is involved,
            so the same posture always produces the same pack.
          </p>
        </div>
        {scoped && (
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs text-gray-500 dark:text-gray-400">Period</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm"
            >
              {periods.map((p, i) => (
                <option key={p} value={p}>
                  {p}
                  {i === 0 ? " (in progress)" : ""}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!scoped ? (
        <p className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
          An evidence pack covers one customer over one period, so pick a customer in the
          header switcher above to generate one. The framework assessment can run
          MSP-wide; evidence can't, because the dates and the audit slice belong to a
          specific tenant.
        </p>
      ) : (
        <>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button
              onClick={preview}
              disabled={!!busy}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg"
            >
              {busy === "preview" ? "Generating…" : "Preview PDF"}
            </button>
            <button
              onClick={zip}
              disabled={!!busy}
              className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg"
            >
              {busy === "zip" ? "Building…" : "Download ZIP"}
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <input
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="Email to (blank = the customer's contact)"
              className="w-64 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
            />
            <button
              onClick={email}
              disabled={!!busy}
              className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg"
            >
              {busy === "email" ? "Sending…" : "Email pack"}
            </button>
          </div>
          <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
            {/* Named explicitly: "email pack" with a blank field is about to mail a
                customer, and that should never be a surprise. */}
            Sending to a blank address uses this customer's registered contact. Preview
            first if you have not sent them one before.
          </p>
        </>
      )}

      {note && (
        <p className="mt-3 text-xs text-emerald-700 dark:text-emerald-400">{note}</p>
      )}
      {err && <p className="mt-3 text-xs text-red-600 dark:text-red-400">{err}</p>}
    </div>
  );
}
